// Fire-and-forget client analytics. `track(name, props)` buffers an event (memory +
// MMKV) and a batcher flushes to `POST /events`. Never throws, never blocks the UI.
// See docs/superpowers/specs/2026-07-29-instrumentation-design.md.

import { nanoid } from 'nanoid/non-secure';
import { mmkv } from '@/storage/mmkv';
import { deviceId } from '@/sync/ids';
import { useDeploymentStore } from '@/store/deploymentStore';
import { CognitoService } from '@/auth/CognitoService';
import { sanitizeProps, type EventName } from './events';

interface StoredEvent {
  name: EventName;
  ts: string;
  props: Record<string, string | number | boolean>;
  deviceId: string;
  userId: string | null;
  sessionId: string;
  appVersion?: string;
}

const BUFFER_KEY = 'analytics.buffer';
const MAX_BUFFER = 500;
const FLUSH_THRESHOLD = 10;
const FLUSH_DEBOUNCE_MS = 5000;

const sessionId = nanoid();
let currentUserId: string | null = null;
let buffer: StoredEvent[] = loadBuffer();
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let flushing = false;

function loadBuffer(): StoredEvent[] {
  try {
    const raw = mmkv.getString(BUFFER_KEY);
    return raw ? (JSON.parse(raw) as StoredEvent[]) : [];
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    mmkv.setString(BUFFER_KEY, JSON.stringify(buffer));
  } catch {
    /* ignore */
  }
}

function appVersion(): string | undefined {
  try {
    // Lazy require so tests / non-native contexts don't need expo-constants.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const Constants = require('expo-constants').default;
    return Constants?.expoConfig?.version;
  } catch {
    return undefined;
  }
}

function base(): string {
  return (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
}

/** Set the signed-in Cognito sub (or null on sign-out). */
export function setAnalyticsUser(sub: string | null): void {
  currentUserId = sub;
}

/** Record an event. Fire-and-forget: never throws, never blocks. */
export function track(name: EventName, props?: object): void {
  try {
    const event: StoredEvent = {
      name,
      ts: new Date().toISOString(),
      props: sanitizeProps(name, props as Record<string, unknown> | undefined),
      deviceId: deviceId(),
      userId: currentUserId,
      sessionId,
      appVersion: appVersion(),
    };
    buffer.push(event);
    if (buffer.length > MAX_BUFFER) buffer = buffer.slice(buffer.length - MAX_BUFFER);
    persist();
    if (buffer.length >= FLUSH_THRESHOLD) {
      void flushNow();
    } else {
      scheduleFlush();
    }
  } catch {
    /* analytics must never break the app */
  }
}

function scheduleFlush(): void {
  if (debounceTimer) return;
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushNow();
  }, FLUSH_DEBOUNCE_MS);
}

/** Flush the buffer now. Resolves whether or not the network call succeeds. */
export async function flushNow(): Promise<void> {
  if (flushing) return;
  if (buffer.length === 0) return;
  const url = base();
  if (!url) return; // no backend configured yet — keep buffering
  flushing = true;
  const batch = buffer.slice();
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = CognitoService.getAccessTokenSync();
    if (token) headers.Authorization = `Bearer ${token}`;
    else headers['x-device-id'] = deviceId();
    const res = await fetch(`${url}/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ events: batch }),
    });
    if (res && (res.ok || res.status === 200)) {
      const sent = new Set(batch);
      buffer = buffer.filter((e) => !sent.has(e));
      persist();
    }
  } catch {
    /* keep the batch buffered; retry on next trigger */
  } finally {
    flushing = false;
  }
}

// --- test-only helpers ---
export function _resetForTest(): void {
  buffer = [];
  currentUserId = null;
  flushing = false;
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}
export function _bufferForTest(): StoredEvent[] {
  return buffer;
}
